import { useGetAllAddressBook } from '@ping/api';
import { generateBase64CoreIcon } from '@corepass/core-blockies';
import AddressIcon from '@ping/assets/Icon/address.svg';
import { t } from '@ping/helpers';
import { SearchField } from '@ping/uikit';
import { Select } from '@ping/uikit/SelectA11Y';
import { clsx } from 'clsx';
import { useEffect, useState } from 'react';
import { userCoreIDSelector, useUserInformationStore } from '@ping/stores/userInformation.store';
import { truncateCoreId } from '@ping/utils';

import style from './style.module.scss';

import type { AddressBookListItemDto } from '@ping/api';
import { type PopoverPlacement } from '@ping/uikit/PrimitivePopover/PrimitivePopoverRoot';

interface IAddressSelectProps extends ICustomizable {
  network?: string;
  selectedKey?: string;
  label?: string;
  isDisabled?: boolean;
  magnet?: IMagnet;
  boundaryRef?: React.MutableRefObject<HTMLElement>;
  onChange?(address: string): void;
  onOpenChange?: (isOpen: boolean) => void;
  onPanelPlacementChange?: (placement: PopoverPlacement) => void;
}

type ContactCryptoGroup = AddressBookListItemDto & { group?: string };
const CORE_CURRENCIES = ['xcb', 'ctn'];

export const AddressSelect = (props: IAddressSelectProps) => {
  const [items, setItems] = useState<ContactCryptoGroup[]>([]);
  const userCoreID = useUserInformationStore(userCoreIDSelector);

  const { data: contacts, isFetching: isContactsFetching } = useGetAllAddressBook({
    query: {
      enabled: Boolean(props.network),
      select: contacts => {
        const resultContact: ContactCryptoGroup[] = contacts.active
          .filter(contact => contact.network.toLowerCase() === props.network?.toLowerCase())
          .map(contact => ({ ...contact, group: t('MY CONTACTS') }));
        if (CORE_CURRENCIES.includes(props.network.toLowerCase())) {
          // TODO: why? value of the network with specified CORE_CURRENCIES is different.
          resultContact.unshift({
            type: props.network,
            label: truncateCoreId(userCoreID),
            id: userCoreID,
            group: t('MY COREPASS WALLET'),
          });
        }
        return resultContact;
      },
    },
  });

  useEffect(() => filterViewItems(''), [isContactsFetching, props.network]);

  const filterViewItems = (value: string) => {
    if (isContactsFetching || !contacts) {
      return;
    }

    const text = value.trim().toLowerCase();
    const items = contacts.filter(
      contact => contact.label?.toLowerCase().includes(text) || contact.walletAddress.includes(text)
    );

    setItems(items);
  };

  const handleOnSelectionChange = (address: string) => {
    filterViewItems('');
    props.onChange?.(address);
  };

  return (
    <Select
      className={clsx(style['address-select'], props.className)}
      items={items}
      isLoading={isContactsFetching}
      selectedKey={props.selectedKey}
      isDisabled={props.isDisabled}
      magnet={props.magnet}
      label={props.label}
      boundaryRef={props.boundaryRef}
      onSelectionChange={handleOnSelectionChange}
      onOpenChange={isOpen => {
        props.onOpenChange?.(isOpen);
        !isOpen && filterViewItems('');
      }}
      onPanelPlacementChange={props.onPanelPlacementChange}
      buttonContent={<AddressIcon />}
      topElement={
        <>
          <SearchField
            className={style['address-select__search']}
            placeholder={t('Search')}
            label={t('Search through addresses')}
            onChange={filterViewItems}
          />
          {!items.length && !isContactsFetching && (
            <p className={style['address-select__not-found']}>{t('Not Found')}</p>
          )}
        </>
      }
    >
      {contact => (
        <Select.Item
          className={style['address-select__item']}
          key={contact.walletAddress}
          aria-label={contact.walletAddress.toUpperCase()}
        >
          {contact.group !== t('MY COREPASS WALLET') ? (
            <>
              <span className={style['address-select__name']}>
                {contact.type.toUpperCase()} - {contact.label || contact.walletAddress}
              </span>
            </>
          ) : (
            <>
              <img
                className={style['address-select__coin-icon']}
                src={generateBase64CoreIcon(userCoreID, 3)}
                alt='core wallet'
              />

              <span className={style['address-select__name']}>{contact.label}</span>
            </>
          )}
        </Select.Item>
      )}
    </Select>
  );
};

export default AddressSelect;
